/**
 * The waiting screen: hand the link over, and get ready while nobody is here yet.
 *
 * Three things changed about this screen, and all three are about *order*.
 *
 * The link is first and the QR code is behind a disclosure (`ui/invite.ts` says why). The clock
 * and "add an hour" moved to the session bar, where they survive somebody joining. And the two
 * things a creator should do while waiting — get their sensors ready, and say where everyone is
 * going — are offered *here*, because standing still with a signal and nothing to do is the best
 * moment in the whole session to do either, and both used to be unreachable until it was too
 * late: onboarding blocked the link, and the meeting point could not be set until somebody had
 * already started walking to the wrong place.
 */

import { SESSION } from '@beeline/shared';

import { el } from '../../lib/dom';
import { otherMembers, selfMember, type SessionState } from '../../lib/session-model';
import { invitePanel, type InvitePanel } from '../../ui/invite';

export interface ShareScreenOptions {
  code: string;
  state: SessionState;
  /** True when this device made the session. A joiner waiting for the host reads differently. */
  host: boolean;
  /**
   * Whether location and compass have been granted (or explicitly declined) yet.
   *
   * A function rather than a value because onboarding can finish while this screen is up, and a
   * screen that still says "allow location" after you have is worse than not offering it.
   */
  ready: () => boolean;
  onSetName: (name: string | null) => void;
  /** Run permission onboarding now, rather than when somebody arrives. */
  onSetUp: () => void;
  /** Open the map picker for the room's shared destination. */
  onChooseRally: () => void;
}

export interface ShareScreen {
  element: HTMLElement;
  update: (state: SessionState) => void;
  destroy: () => void;
}

const CONNECTION_TEXT: Record<SessionState['connection'], string> = {
  connecting: 'Connecting…',
  open: 'Waiting for them to open it…',
  reconnecting: 'Reconnecting…',
  closed: 'Not connected',
};

export function shareScreen(options: ShareScreenOptions): ShareScreen {
  const shareUrl = `${window.location.origin}/s/${options.code}`;
  let state = options.state;

  /** The name this device has given itself, which is the field below, not somebody else's. */
  const ownName = (from: SessionState): string | null => selfMember(from)?.name ?? null;

  const waitingLabel = el('p', { class: 'share__waiting' });

  /* --- naming ----------------------------------------------------------- */

  const nameInput = el('input', {
    class: 'field__input',
    id: 'self-name',
    type: 'text',
    name: 'name',
    autocomplete: 'given-name',
    enterkeyhint: 'done',
    maxlength: String(SESSION.maxNameLength),
    placeholder: 'Optional',
    value: ownName(state) ?? '',
  });

  // Committed on blur and on Enter rather than per keystroke: this is one small write, not a
  // live-sync field, and a socket message per character would be silly.
  const commitName = (): void => {
    const next = nameInput.value.trim() || null;
    if (next === ownName(state)) return;
    options.onSetName(next);
  };
  nameInput.addEventListener('blur', commitName);
  nameInput.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key === 'Enter') {
      event.preventDefault();
      nameInput.blur();
    }
  });

  /* --- sharing ---------------------------------------------------------- */

  const invite: InvitePanel = invitePanel({
    url: shareUrl,
    message: 'Follow this to find me — no app needed.',
  });

  /* --- while you wait --------------------------------------------------- */

  const setUpButton = el(
    'button',
    {
      class: 'button button--ghost button--block',
      type: 'button',
      'data-action': 'set-up-sensors',
    },
    'Allow location and compass',
  );
  setUpButton.addEventListener('click', () => options.onSetUp());

  const rallyButton = el(
    'button',
    { class: 'button button--ghost button--block', type: 'button', 'data-action': 'set-rally' },
    'Set a meeting point',
  );
  rallyButton.addEventListener('click', () => options.onChooseRally());

  const readyNote = el('p', { class: 'field__hint' });

  const meanwhile = el(
    'section',
    { class: 'share__meanwhile' },
    el('h2', { class: 'field__label', text: 'While you wait' }),
    setUpButton,
    rallyButton,
    readyNote,
  );

  /* --- layout ----------------------------------------------------------- */

  const element = el(
    'main',
    { class: 'screen share' },
    el(
      'header',
      { class: 'share__head' },
      el('h1', { class: 'screen__title', text: 'Send this to them' }),
      el('p', {
        class: 'screen__body',
        text: 'They open it, allow location, and you both get an arrow.',
      }),
    ),

    invite.element,

    el(
      'label',
      { class: 'field field--inline' },
      el('span', { class: 'field__label', text: 'Your first name' }),
      nameInput,
      el('span', { class: 'field__hint', text: 'So they see who is looking for them.' }),
    ),

    meanwhile,
    el('footer', { class: 'share__foot' }, waitingLabel),
  );

  /* --- rendering -------------------------------------------------------- */

  const render = (): void => {
    // In a room built for more than two the wait is not for *them*, it is for the next person,
    // and a screen that says "waiting for them" while four people are already inside is wrong.
    const waiting = otherMembers(state).length;
    waitingLabel.textContent =
      state.connection === 'open' && state.capacity > 2
        ? waiting === 0
          ? `Nobody has opened it yet — room for ${state.capacity - 1} more`
          : `${waiting} here · room for ${state.capacity - 1 - waiting} more`
        : options.host || state.connection !== 'open'
          ? CONNECTION_TEXT[state.connection]
          : 'Waiting for them to come back…';
    waitingLabel.classList.toggle('share__waiting--warn', state.connection === 'reconnecting');

    const ready = options.ready();
    setUpButton.hidden = ready;
    readyNote.textContent = ready
      ? 'Your sensors are ready. The arrow starts the moment somebody joins.'
      : 'Two taps now means no fumbling when they arrive.';

    rallyButton.textContent = state.rally
      ? `Meeting point: ${state.rally.label} — change it`
      : 'Set a meeting point';
  };

  render();

  return {
    element,
    update: (next) => {
      const previousName = ownName(state);
      state = next;
      // Do not fight the user's cursor: only write into the field when the value changed
      // elsewhere (a reconnect replaying hello) and they are not typing in it.
      const nextName = ownName(next);
      if (nextName !== previousName && document.activeElement !== nameInput) {
        nameInput.value = nextName ?? '';
      }
      render();
    },
    destroy: () => invite.destroy(),
  };
}
